import type { EnforcedPlanFeature } from '@bookora/shared';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  THE ONE MAP of where each enforced plan feature is locked in the panel
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Keys per C1–C3 (`lotInventory`, `apiAccess`, `branches`). A target is
 * `<section>:<tab>` for a tab, `route:<path>` for a child route, or
 * `<section>:<name>` with `kind: 'mode' | 'card'` for a part of a screen. The
 * screens that lock something import the feature from HERE rather than naming
 * it themselves — the inventory and session screens (panel-branches-ops), the
 * integrations tab (clinic-api) and this stream's accounting, CRM and
 * dashboard — so «which plan feature is this screen» has one answer, and the
 * sidebar's «پلن بالاتر» badge is derived from the same entries.
 *
 * NOT in the map, on purpose:
 *
 *  · P&L, «درآمد», «مصرف مواد» and «گزارش‌ها» (owner round 4, C5). No GET is
 *    plan-refused: those tabs render on every plan, and the server withholds
 *    only the profit LINES (`planHiddenFields`) and the profit reports
 *    (`locked`, `planHiddenColumns`), which the tabs draw as «در پلن …».
 *  · «دریافت‌های بیماران» — recording a patient's payment is open on every plan.
 *  · `aiImport` — gated by its own entitlement and quota (the import screens
 *    name the plan with more pages), not by a tab.
 *  · `branches` — an «add a branch» lock on the branches tab (a card), not a
 *    tab: editing the primary branch works on every plan.
 *
 * `treatment: 'hidden'` drops the target entirely instead of drawing a lock —
 * the dashboard's expiry groups, which have nothing to explain on a plan with
 * simple stock (low stock stays).
 */

export type SectionFeatureKind = 'tab' | 'route' | 'mode' | 'card';

export interface SectionFeatureTarget {
  feature: EnforcedPlanFeature;
  /** Computed analytics have no read-only mode (`RequirePlanFeature computed`). */
  computed?: boolean;
  /** `hidden` targets drop out entirely instead of showing a lock. */
  treatment?: 'lock' | 'hidden';
  /** What the key names: a tab (counts toward the sidebar badge), a route, a mode, a card. */
  kind: SectionFeatureKind;
}

const MAP = Object.freeze({
  'accounting:expenses': { feature: 'accounting', kind: 'tab' },
  'accounting:payroll': { feature: 'payroll', kind: 'tab' },
  'accounting:revenue-shares': { feature: 'revenueShare', kind: 'tab' },
  'crm:campaigns': { feature: 'campaigns', kind: 'tab' },
  'crm:loyalty': { feature: 'loyalty', kind: 'tab' },
  'inventory:lots': { feature: 'lotInventory', kind: 'tab' },
  'settings:integrations': { feature: 'apiAccess', kind: 'tab' },
  'dashboard:expiry': { feature: 'lotInventory', kind: 'card', treatment: 'hidden' },
  'route:inventory/lots/:lotId': { feature: 'lotInventory', kind: 'route' },
  'inventory:receive-mode': { feature: 'lotInventory', kind: 'mode' },
  'sessions:lot-choice': { feature: 'lotInventory', kind: 'mode' },
} as const satisfies Readonly<Record<string, SectionFeatureTarget>>);

export type SectionFeatureKey = keyof typeof MAP;

/** Every target, by key (string-indexable for a caller that builds the key). */
export const SECTION_FEATURES: Readonly<Record<string, SectionFeatureTarget>> = MAP;

/** The feature a target is locked by. */
export function sectionFeature(target: SectionFeatureKey): EnforcedPlanFeature {
  return MAP[target].feature;
}

/** The feature of `<section>:<tab>`, or undefined when that tab is not plan-locked. */
export function tabFeature(section: string, tab: string): EnforcedPlanFeature | undefined {
  const entry = SECTION_FEATURES[`${section}:${tab}`];
  return entry?.kind === 'tab' ? entry.feature : undefined;
}

/**
 * The features a section has LOCKABLE TABS for — what the sidebar badge
 * watches (owner round 6: a section shows «پلن بالاتر» when at least one of
 * its tabs is locked). Hidden targets, modes, cards and routes do not count:
 * a section whose every tab is open is not «in a higher plan».
 */
export function sectionTabFeatures(section: string): readonly EnforcedPlanFeature[] {
  const out: EnforcedPlanFeature[] = [];
  for (const [key, target] of Object.entries(SECTION_FEATURES)) {
    if (target.kind !== 'tab' || target.treatment === 'hidden') continue;
    if (!key.startsWith(`${section}:`)) continue;
    if (!out.includes(target.feature)) out.push(target.feature);
  }
  return out;
}
